import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../Redux/Actions/SnackbarActions/SnackbarActions";
import PharmacySnackbar from "../../../Utils/Snackbars/SnackbarUtil";
import { PLContainer } from "../../../Utils/styledComponents";
import { usePrint } from "../../../Utils/Templates/usePrint";
import { BackButton } from "../../../Utils/Buttons/DataExportationButton";
import { PrintPdfButtons } from "./PrintPdfButtons";
import companyAddress from "../../../Helpers/companyAddress.json";
import Card from "@material-ui/core/Card";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import ReusableDashboardHeader from "../../../Containers/Dasboard/ReusableDashboardHeader";
import { history } from "../../../Helpers/history";
import { useQuery } from "react-query";
import axios from "axios";
import config from "../../../Helpers/config.json";
import { useCompany } from "../../../Context/company-context";
import { LoadingGif } from "../../../Utils/Loader";
import { NoTransaction } from "../COA/ReusableJournalTable";
import { formatAmount } from "../../../Utils/formatAmount";
import { reverseDate, convertDate } from "../../../Utils/ConvertDate";

const useStyles = makeStyles((theme) => ({
  sectionHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: "12px 16px",
    borderBottom: "2px solid #ddd",
  },
  itemRow: {
    "& td": {
      padding: "8px 16px",
    },
  },
  amountCell: {
    textAlign: "right",
    width: "180px",
    paddingRight: "24px",
  },
  totalRow: {
    borderTop: "3px double #000",
    fontWeight: "bold",
    fontSize: "1.1rem",
    backgroundColor: "#fafafa",
  },
  netChangeRow: {
    borderTop: "4px double #000",
    fontWeight: "bold",
    fontSize: "1.2rem",
    backgroundColor: "#eef5fd",
  },
  notesSection: {
    marginTop: "20px",
    padding: "10px 16px",
    fontSize: "0.9rem",
    lineHeight: "1.5",
  },
}));

const CashFlowStatementNew = ({ actions, snackbars }) => {
  const classes = useStyles();
  const { state } = useCompany();
  const { logo } = state;
  const { img_url: defaultImage } = companyAddress;

  // Helper to format amount with parentheses for negatives (no minus sign)
  const formatAmountWithParentheses = (amount) => {
    if (amount === undefined || amount === null) return formatAmount(0);
    const formatted = formatAmount(Math.abs(amount)); // Get positive formatted string
    return amount < 0 ? `(${formatted})` : formatted;
  };

  // Dynamic default dates: Start of current year → Today
  const today = convertDate(); // e.g., "2026-01-02"
//   const startOfYear = "2026-01-01"; // Since current date is Jan 02, 2026

  const [dates, setDates] = useState({
    startDate: reverseDate(today),
    endDate: reverseDate(today),
  });

  const { isLoading, isError, isSuccess, data } = useQuery(
    ["cashFlowStatement", dates],
    async () => {
      const response = await axios.get(
        `${config.epharmUrl}/accounting/cash_flow_statement`,
        {
          params: {
            start_date: dates.startDate,
            end_date: dates.endDate,
          },
        }
      );
      return response.data?.data || {};
    },
    { retry: 1 }
  );

  const { open, variant, message } = snackbars;
  const { componentRef, handlePrint } = usePrint("CashFlowStatement");

  const cashFlow = data?.cash_flow_statement || {};
  const generatedOn = data?.generated_on || "";
  const period = data?.period || `${dates.startDate} to ${dates.endDate}`;

  const handleChangeDate = (e) => {
    setDates({ ...dates, [e.target.name]: e.target.value });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Cash Flow Statement"
        heading="Accounting"
        subHeading="Cash Flow Statement"
        link={history.location.pathname}
      />
      <PharmacySnackbar message={message} open={open} variant={variant} />
      <div className="d-flex justify-content-start align-items-center ml-4 mt-3">
        <BackButton to="/allreports" text="Reports" />
      </div>

      <PLContainer>
        {isLoading && (
          <div className="text-center mt-5">
            <LoadingGif />
          </div>
        )}

        {isSuccess && cashFlow ? (
          <Card className="mx-2 my-3">
            <div
              style={{
                padding: "8px 16px",
                borderBottom: "1px solid rgba(224,224,224,1)",
              }}
              className="d-flex justify-content-between align-items-center"
            >
              <div className="d-flex">
                <div className="form-group d-flex align-items-center">
                  <label htmlFor="startDate">From&nbsp;</label>
                  <input
                    type="date"
                    name="startDate"
                    value={dates.startDate}
                    onChange={handleChangeDate}
                    className="form-control"
                  />
                </div>
                <div className="form-group d-flex align-items-center ml-4">
                  <label htmlFor="endDate">To&nbsp;</label>
                  <input
                    type="date"
                    name="endDate"
                    value={dates.endDate}
                    onChange={handleChangeDate}
                    className="form-control"
                  />
                </div>
              </div>
              <div>
                <PrintPdfButtons downloadPdf={() => {}} handlePrint={handlePrint} />
              </div>
            </div>

            <div className="p-3">
              <div ref={componentRef}>
                {/* Header */}
                <table className="table table-sm table-borderless prescription-table w-100">
                  <tbody>
                    <tr>
                      <td align="center">
                        <img
                          className="img-fluid img-thumbnail border-0 w-25 h-25"
                          src={logo ?? defaultImage}
                          alt="company logo"
                        />
                      </td>
                    </tr>
                    <tr>
                      <td align="center">
                        <h4><strong>CASH FLOW STATEMENT</strong></h4>
                      </td>
                    </tr>
                    <tr>
                      <td align="center">
                        <h6><strong>{period}</strong></h6>
                      </td>
                    </tr>
                    <tr>
                      <td align="center">
                        <small>Generated on: {formatDateTime(generatedOn)}</small>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <Table size="small">
                  <TableBody>
                    {/* Operating Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash Flows from Operating Activities
                      </TableCell>
                    </TableRow>
                    {cashFlow.operating_activities?.items?.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash from operating activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.operating_activities?.net_cash_from_operating)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Investing Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash Flows from Investing Activities
                      </TableCell>
                    </TableRow>
                    {cashFlow.investing_activities?.items?.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash from investing activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.investing_activities?.net_cash_from_investing)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Financing Activities */}
                    <TableRow>
                      <TableCell colSpan={2} className={classes.sectionHeader}>
                        Cash Flows from Financing Activities
                      </TableCell>
                    </TableRow>
                    {cashFlow.financing_activities?.items?.map((item, idx) => (
                      <TableRow key={idx} className={classes.itemRow}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell className={classes.amountCell}>
                          {formatAmountWithParentheses(item.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Net cash from financing activities</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.financing_activities?.net_cash_from_financing)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Net Change */}
                    <TableRow className={classes.netChangeRow}>
                      <TableCell><strong>Net change in cash and cash equivalents</strong></TableCell>
                      <TableCell className={classes.amountCell}>
                        <strong>{formatAmountWithParentheses(cashFlow.net_change_in_cash)}</strong>
                      </TableCell>
                    </TableRow>

                    {/* Total Cash Movement */}
                    {cashFlow.total_cash_movement !== undefined && (
                      <TableRow className={classes.netChangeRow}>
                        <TableCell><strong>Total cash movement</strong></TableCell>
                        <TableCell className={classes.amountCell}>
                          <strong>{formatAmountWithParentheses(cashFlow.total_cash_movement)}</strong>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Notes */}
                <div className={classes.notesSection}>
                  <h6><strong>Notes:</strong></h6>
                  <ul>
                    <li>Negative amounts are shown in parentheses, as is standard in financial statements.</li>
                    <li>This reflects a net outflow of cash, primarily driven by inventory purchases and operating adjustments.</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        ) : !isLoading ? (
          <NoTransaction text="cash flow data" />
        ) : null}

        {isError && <div className="text-center text-danger">Failed to load cash flow statement. Please try again.</div>}
      </PLContainer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbars,
});

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) },
});

export default connect(mapStateToProps, mapDispatchToProps)(CashFlowStatementNew);